/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useMemo, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { breakpoints } from '@birdi/theme';
import { useNavigate, Link } from 'react-router';
import { MapComponent } from './map-component';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

export const MapCoverage: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();
  const [maps, setMaps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!meta.organisation?.id) {
          setMaps([]);
          setLoading(false);
          return;
        }
        const res = await mapsAPI.getOrganisationMaps(
          stroot('duej0i'),
          meta.workspace?.id || meta.organisation.id,
          { noCover: 'yes' },
        );
        setMaps(res.body || []);
      } catch (e) {
        setMaps([]);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id]);

  // might need to use 'locationAddress' instead of suburb

  // Only use maps with a valid center and location name
  const mapsWithCenter = maps.filter(m =>
    m.location &&
    m.location.name &&
    Array.isArray(m.location.center) &&
    m.location.center.length === 2 &&
    typeof m.location.center[0] === 'number' &&
    typeof m.location.center[1] === 'number'
  );

  // Group by location name
  const suburbCounts: Record<string, number> = {};
  mapsWithCenter.forEach(m => {
    const suburb = m.location.name;
    if (suburb) {
      suburbCounts[suburb] = (suburbCounts[suburb] || 0) + 1;
    }
  });
  const suburbList = Object.entries(suburbCounts).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  // Stable reference so MapComponent doesn't re-run marker logic on every parent re-render (e.g. hover)
  const mapsForMap = useMemo(() => {
    const withCenter = maps.filter(
      (m) =>
        m.location &&
        m.location.name &&
        Array.isArray(m.location.center) &&
        m.location.center.length === 2 &&
        typeof m.location.center[0] === 'number' &&
        typeof m.location.center[1] === 'number',
    );
    return withCenter.map((m) => ({
      ...m,
      center: m.location.center,
      suburb: m.location.name,
    }));
  }, [maps]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'map-coverage'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Map coverage</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
          `}
        >
          Geospatial distribution of your maps
        </div>
        {!loading && mapsWithCenter.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              background-image: url('https://birdi-public.s3-ap-southeast-2.amazonaws.com/map-coverage-background-2.jpg');
              background-size: cover;
              padding: 0 1em;
            `}
          >
            <div>
              <strong>Your map coverage is currently empty:</strong> Once you
              upload and process datasets, your map activity will appear here as
              a global view of coverage.
            </div>
            <div
              css={css`
                margin-top: 0.5em;
              `}
            >
              <a
                href="https://help.birdi.io/en/articles/10416302-how-to-upload-media-to-your-birdi-2-0-map"
                css={css`
                  color: ${theme.deepOcean};
                  text-decoration: underline;
                `}
                target="_blank"
              >
                View upload guide
              </a>
            </div>
          </div>
        )}
        {!loading && mapsWithCenter.length > 0 && (
          <>
            <div css={css`background: ${theme.mono0};`}>
              <MapComponent maps={mapsForMap} />
            </div>
            <div
              css={css`
                margin-top: 1em;
              `}
            >
              {' '}
              {/* Suburb list */}
              {suburbList.map(([suburb, count]) => {
                // Find all maps in this suburb
                const mapsInSuburb = mapsWithCenter.filter(m => m.location.name === suburb);
                const lats = mapsInSuburb.map(m => m.location.center[1]);
                const lngs = mapsInSuburb.map(m => m.location.center[0]);
                const bbox = [
                  [Math.min(...lngs), Math.min(...lats)],
                  [Math.max(...lngs), Math.max(...lats)],
                ];
                // Use the center of the bbox as the center
                const center = [
                  (bbox[0][0] + bbox[1][0]) / 2,
                  (bbox[0][1] + bbox[1][1]) / 2,
                ];
                return (
                  <div
                    key={suburb}
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      padding: 0 0.5em;
                    `}
                  >
                    <button
                      type="button"
                      // disabled
                      onClick={() => {
                        posthog?.capture(
                          'dashboard map coverage location clicked',
                          { suburb },
                        );
                        navigate('/new-maps/all', {
                          state: {
                            location: {
                              name: suburb,
                              suburbName: suburb,
                              center,
                              bbox,
                            },
                          },
                        });
                      }}
                      css={css`
                        background: none;
                        border: none;
                        color: ${theme.deepOcean};
                        text-decoration: underline;
                        text-align: left;
                        cursor: pointer;
                        font-size: 1em;
                        padding: 0;
                        //
                        // cursor: default;
                        // color: ${theme.mono60};
                      `}
                    >
                      {suburb}
                    </button>
                    <span
                      css={css`
                        color: ${theme.midnight};
                      `}
                    >
                      {count} map{count > 1 ? 's' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>
      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          onClick={() => navigate('/new-maps/all')}
          css={css`
            width: 100%;
            color: ${theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: pointer;
            background: ${theme.mono0};
          `}
        >
          View all maps
        </button>
      </div>
    </div>
  );
};
